import React, {createContext, useReducer} from 'react';
import {gaEvent} from 'app/utilities/analytics/tracking';
import gaParams from 'app/utilities/analytics/constants';
import {isOpen} from 'app/utilities/ActivityHoursCheck';

export type OrdersListResult = {
  subscriberNumber: string;
  simNumber: string;
  time: string;
  status: 'CAN_START_NP' | 'IN_PROGRESS' | 'UNABLE_START_NP' | 'FINISH_NP';
  isEsim: boolean;
  // CAN_START_NP – יש אפשרות לבצע ניוד
  // IN_PROGRESS – נמצא באמצע תהליך ניוד
  // UNABLE_START_NP – אין אפשרות לבצע ניוד
  // FINISH_NP – ניוד הסתיים
};

type Links = {
  newLinePurchaseUrl: string;
  personalAreaUrl: string;
  endButtonUrl: string;
};

export type Stage = 'Idle' | 'Phone' | 'Code' | 'Envelope' | 'Activate' | 'Failed' | 'Success' | 'None';
export type ActicationStage = 'Idle' | 'Activating' | 'Progress' | 'Activated';

type State = {
  stage: Stage;
  links: Links;
  phone: string | null;
  ticket: string | null;
  verification: string | null;
  result: OrdersListResult | null;
  activation: ActicationStage;
  isNiudCodeRequired: boolean;
};

const initialState: State = {
  stage: 'Idle',
  links: null,
  phone: null,
  ticket: null,
  verification: null,
  result: null,
  activation: 'Idle',
  isNiudCodeRequired: true,
};

type Action =
  | {type: 'debug'; stage?: Stage; activation?: ActicationStage}
  | {type: 'init'}
  | {type: 'links'; links: Links}
  | {type: 'ticket'; ticket: string; phone: string}
  | {type: 'code'; value: boolean}
  | {type: 'orders'; verification: string; result: OrdersListResult}
  | {type: 'activate-start'}
  | {type: 'activate-time'; result: OrdersListResult}
  | {type: 'activate-timeout'}
  | {type: 'activate'; status: boolean};

const gaAction = (param: {category: string; eventType: string; eventParams: string}) =>
  gaEvent(param.category, param.eventType, param.eventParams);

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'debug':
      // const testResult: OrdersListResult =
      //   action.stage !== 'Activate'
      //     ? state.result
      //     : {
      //         simNumber: '1238120342810321',
      //         status: 'CAN_START_NP',
      //         subscriberNumber: '0559415798',
      //         time: '05/05/2020',
      //       };
      return {...state, stage: action.stage ?? state.stage, activation: action.activation ?? state.activation, result: null};
    case 'links':
      return {...state, links: action.links};
    case 'init':
      gaAction(gaParams.MOBILITY_SIM_INIT);
      return {...state, stage: 'Phone', phone: null, verification: null, result: null, activation: 'Idle'};
    case 'ticket':
      gaAction(gaParams.MOBILITY_SIM_OTP);
      return {...state, ticket: action.ticket, phone: action.phone, stage: 'Code'};
    case 'code':
      if (action.value) {
        gaAction(gaParams.MOBILITY_SIM_ENVELOPE_CODE);
        return {...state, stage: 'Envelope'};
      } else {
        gaAction(gaParams.MOBILITY_SIM_OTP_ERROR);
        return state;
      }

    case 'orders':
      const result = action.result;
      if (result == null) {
        gaAction(gaParams.MOBILITY_SIM_ACTIVATION_UNABLE);
        return {...state, stage: 'None'};
      }
      switch (result.status) {
        case 'CAN_START_NP':
          gaAction(gaParams.MOBILITY_SIM_ACTIVATION_START);
          delete result.time;
          return {...state, stage: 'Activate', result, activation: 'Idle', verification: action.verification};
        case 'IN_PROGRESS':
          gaAction(gaParams.MOBILITY_SIM_ACTIVATION_IN_PROGRESS);
          return {...state, stage: 'Activate', result, activation: 'Activated'};
        case 'UNABLE_START_NP':
          gaAction(gaParams.MOBILITY_SIM_ACTIVATION_UNABLE);
          return {...state, stage: 'None', result: null};
        case 'FINISH_NP':
          gaAction(gaParams.MOBILITY_SIM_ACTIVATION_FINISHED);
          return {...state, stage: 'Success', result: null};
      }
    case 'activate-start':
      return {...state, activation: 'Activating'};
    case 'activate-time':
      gaAction(gaParams.MOBILITY_SIM_ACTIVATION_SUCSESS);
      return {...state, stage: 'Activate', result: action.result, activation: 'Activated'};
    case 'activate-timeout':
      gaAction(gaParams.MOBILITY_SIM_ACTIVATION_SUCSESS_NO_TIME);
      return {...state, activation: 'Activated'};
    case 'activate':
      if (action.status) {
        const isOpened = isOpen('08:00-20:00', '08:00-12:00', false);
        if (!isOpened) gaAction(gaParams.MOBILITY_SIM_ACTIVATION_SUCSESS_NO_TIME);
        const activation = isOpened ? 'Progress' : 'Activated';
        return {...state, stage: 'Activate', activation};
      } else {
        gaAction(gaParams.MOBILITY_SIM_ACTIVATION_FAILURE);
        return {...state, stage: 'Failed', activation: 'Idle'};
      }
  }
};

const SimActivationContext = createContext<[State, React.Dispatch<Action>]>(null);

export const SimActivationProvider: React.FC = ({children}) => {
  const ctx = useReducer(reducer, initialState);
  return <SimActivationContext.Provider value={ctx}>{children}</SimActivationContext.Provider>;
};

export default SimActivationContext;
